import { CHART_THRESHOLD_FILL_CANVAS_ATTR } from '@toggle/design-system/src/charts/constants/chart-constants';

const isSameOrigin = (url: string | null): boolean => {
  if (!url) {
    return true;
  }

  const stylesheetUrl = new URL(url, window.location.href);
  return stylesheetUrl.origin === window.location.origin;
};

export const extractStylesheetRules = async (
  stylesheet: CSSStyleSheet
): Promise<string> => {
  if (isSameOrigin(stylesheet.href)) {
    try {
      if (stylesheet.cssRules) {
        return Array.from(stylesheet.cssRules).reduce(
          (acc, rule, index) => acc + (index > 0 ? '\n' : '') + rule.cssText,
          ''
        );
      }
    } catch {
      return '';
    }
  }

  if (stylesheet.href) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(stylesheet.href, {
        signal: controller.signal,
      });
      if (response.ok) {
        const css = await response.text();
        // Prevent fetched CSS from breaking out of the injected <style> block
        return css.replace(/<\/style/gi, '<\\/style');
      }
    } catch {
      return '';
    } finally {
      clearTimeout(timeoutId);
    }
  }

  return '';
};

export const getCanvasDataUrl = (
  canvas: HTMLCanvasElement,
  shouldInvert: boolean
): string => {
  if (!shouldInvert) {
    return canvas.toDataURL('image/png');
  }

  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = canvas.width;
  tempCanvas.height = canvas.height;
  const ctx = tempCanvas.getContext('2d');

  if (!ctx) {
    return canvas.toDataURL('image/png');
  }

  const isThresholdFillCanvas = canvas.hasAttribute(
    CHART_THRESHOLD_FILL_CANVAS_ATTR
  );

  ctx.filter = isThresholdFillCanvas
    ? 'invert(1) hue-rotate(180deg)'
    : 'invert(1)';
  ctx.drawImage(canvas, 0, 0);

  return tempCanvas.toDataURL('image/png');
};

export const copyCanvasStylesToImage = (
  canvas: HTMLCanvasElement,
  img: HTMLImageElement
) => {
  const computedStyle = window.getComputedStyle(canvas);

  for (let i = 0; i < computedStyle.length; i += 1) {
    const property = computedStyle.item(i);
    img.style.setProperty(
      property,
      computedStyle.getPropertyValue(property),
      computedStyle.getPropertyPriority(property)
    );
  }

  img.style.width = computedStyle.width;
  img.style.height = computedStyle.height;
  img.className = canvas.className;
};
